import { formatRating } from '@common/helpers/rating';
import Rating from '@react/shared/components/Rating';
import cx from 'classnames';
import { bool, func, number, string } from 'prop-types';
import clsxm from 'sly/lib/clsxm';
import { Button } from 'sly/system';

function ReviewText({ numReviews, textClassName, reviewsText }) {
  if (numReviews === 0) {
    return null;
  }
  return (
    <>
      <div
        className={cx(textClassName, 'underline', 'hidden md:block')}
        data-tp-id='gen-div-abae4dde-bd53-4616-9671-28b9533ae415'
      >
        ({numReviews}
        {reviewsText && (numReviews === 1 ? ' review' : ' reviews')})
      </div>
      <div className={cx(textClassName, 'underline md:hidden')}>
        ({numReviews})
      </div>
    </>
  );
}

const RatingBody = ({
  rating,
  numReviews,
  seedId,
  color,
  variation,
  size,
  reviewsText,
  wrapperClassName,
  showNotYetRatedText,
  onClick,
  textClassName,
  reduceLoopCount,
}) => {
  let ratingText = '';
  if (rating > 0) {
    ratingText = formatRating(rating);
  } else if (showNotYetRatedText) {
    ratingText = 'Not yet rated';
  }
  return (
    <div
      className={clsxm(
        'cursor-pointer gap-xxs',
        wrapperClassName,
        'flex items-center  hover:no-underline'
      )}
      data-tp-id='gen-div-d776e69e-4b42-4251-8682-9419a3b47b84'
      onClick={onClick}
    >
      {rating > 0 && (
        <>
          <Rating
            seedId={seedId}
            value={rating}
            color={color}
            variation={variation}
            font={size}
            className={cx(reduceLoopCount && 'hidden md:block')}
          />
          {reduceLoopCount && (
            <Rating
              seedId={seedId}
              value={rating}
              color={color}
              variation={variation}
              font={size}
              className='md:hidden'
              innerClassName='w-[1rem]'
              viewBox='0 0 24 24'
              loopCount={1}
            />
          )}
        </>
      )}
      <div
        className='font-b-s mr-xxs ml-1 h-[18px] font-medium text-slate-base'
        data-tp-id='gen-div-a14be57b-141b-4cff-a7e6-f68693737edb'
      >
        {rating > 0 && ratingText}
      </div>
      <ReviewText
        numReviews={numReviews}
        reviewsText={reviewsText}
        textClassName={textClassName}
      />
      {rating === 0 && numReviews === 0 && showNotYetRatedText && (
        <>
          <Rating seedId={seedId} value={0} />
          <div
            className={textClassName}
            data-tp-id='gen-div-abae4dde-bd53-4616-9671-28b9533ae415'
          >
            No reviews yet
          </div>
        </>
      )}
    </div>
  );
};

/**
 * @param {{
 *   rating: number,
 *   numReviews?: number,
 *   size?: string,
 *   color?: string,
 *   variation?: string,
 *   goToReviews?: (...args: any[]) => void,
 *   seedId?: string,
 *   reviewsText?: boolean,
 *   communitySlug?: string,
 *   wrapperClassName?: string,
 *   showNotYetRatedText?: boolean,
 *   onClick?: (...args: any[]) => void,
 *   linkHref?: string,
 *   reduceLoopCount?: boolean,
 *   textClassName?: string,
 * }} props
 */
const CommunityRating = ({
  rating,
  numReviews,
  size = 'body-medium',
  color = 'primary',
  variation,
  goToReviews,
  seedId,
  reviewsText = false,
  communitySlug,
  wrapperClassName,
  showNotYetRatedText = false,
  onClick = () => {},
  linkHref,
  reduceLoopCount = true,
  textClassName = 'text-viridian-base',
}) => {
  const buttonProps = {
    href: linkHref,
    onClick: goToReviews,
    eventProps: {
      text: 'Reviews',
      location: 'summary action list',
      communitySlug,
    },
  };

  if (!goToReviews && !linkHref) {
    return (
      <RatingBody
        rating={rating}
        numReviews={numReviews}
        seedId={seedId}
        color={color}
        variation={variation}
        size={size}
        reviewsText={reviewsText}
        showNotYetRatedText={showNotYetRatedText}
        wrapperClassName={wrapperClassName}
        onClick={onClick}
        reduceLoopCount={reduceLoopCount}
        textClassName={textClassName}
      />
    );
  }

  return (
    <Button
      variant='custom'
      className={clsxm(
        wrapperClassName,
        'flex items-center gap-xxs !p-0 text-viridian-base hover:no-underline'
      )}
      {...buttonProps}
      data-tp-id='gen-Link-82d989a7-8141-43c8-a98b-d8008e8ed53b'
    >
      <RatingBody
        rating={rating}
        numReviews={numReviews}
        seedId={seedId}
        color={color}
        variation={variation}
        size={size}
        reviewsText={reviewsText}
        showNotYetRatedText={showNotYetRatedText}
        reduceLoopCount={reduceLoopCount}
      />
    </Button>
  );
};

CommunityRating.propTypes = {
  description: string,
  rating: number.isRequired,
  numReviews: number,
  goToReviews: func,
  size: string,
  color: string,
  variation: string,
  className: string,
  seedId: string,
  reviewsText: bool,
  communitySlug: string,
  wrapperClassName: string,
  showNotYetRatedText: bool,
  onClick: func,
  textClassName: string,
  linkHref: string,
  reduceLoopCount: bool,
};

export default CommunityRating;
